import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Edit, Trash2, Search, Filter, Download, MessageCircle, CheckCircle, Send, FileText, AlertCircle, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { exportToExcel, formatBillsForExport } from '../utils/excelExport';

export default function ViewBills() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [branches, setBranches] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    branch_id: '',
    theater_id: '',
    start_date: '',
    end_date: '',
    flagged_only: false,
    date_preset: 'all' // 'all', 'last7days', 'thismonth', 'custom'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [auditNotes, setAuditNotes] = useState('');
  const [auditFlagged, setAuditFlagged] = useState(false);
  const [expandedBills, setExpandedBills] = useState({});
  const [expandedDetails, setExpandedDetails] = useState({});
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const isRegionalManager = user?.role === 'regional_manager';

  useEffect(() => {
    if (!user) return;
    fetchBills();
    fetchSettings();
    if (user.role === 'admin' || user.role === 'auditor' || user.role === 'regional_manager') {
      fetchBranches();
    }
  }, [user?.id]);

  // Note: Removed auto-filter useEffect - filters now only apply when "Apply Filters" button is clicked

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      
      // For auditors or regional managers, filter to only assigned branches
      if ((user?.role === 'auditor' || user?.role === 'regional_manager') && user?.assigned_branches) {
        const assignedBranches = response.data.filter(branch => 
          user.assigned_branches.includes(branch.id)
        );
        setBranches(assignedBranches);
      } else {
        setBranches(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const fetchBills = async () => {
    setLoading(true);
    try {
      let url = '/bills';
      const params = new URLSearchParams();
      if (filters.branch_id && filters.branch_id !== 'all') params.append('branch_id', filters.branch_id);
      if (filters.theater_id && filters.theater_id !== 'all') params.append('theater_id', filters.theater_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.flagged_only) params.append('flagged_only', 'true');
      if (params.toString()) url += `?${params.toString()}`;

      const response = await api.get(url);
      setBills(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to fetch bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchTheaters = async (branchId) => {
    try {
      const response = await api.get(`/theaters?branch_id=${branchId}`);
      setTheaters(response.data);
    } catch (error) {
      console.error('Failed to fetch theaters');
    }
  };

  const handleDatePresetChange = (preset) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    if (preset === 'last7days') {
      const last7Days = new Date(today);
      last7Days.setDate(today.getDate() - 7);
      startDate = last7Days.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (preset === 'thismonth') {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (preset === 'all') {
      // Clear dates for "All dates" option
      startDate = '';
      endDate = '';
    }

    setFilters({
      ...filters,
      date_preset: preset,
      start_date: startDate,
      end_date: endDate
    });
  };

  const handleResetFilters = () => {
    setFilters({
      branch_id: '',
      theater_id: '',
      start_date: '',
      end_date: '',
      flagged_only: false,
      date_preset: 'all'
    });
    setSearchTerm('');
  };

  const handleDelete = async (billId) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;

    try {
      await api.delete(`/bills/${billId}`);
      setBills(prev => prev.filter(b => b.id !== billId));
      toast.success('Bill deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bill');
    }
  };

  const handleExport = () => {
    try {
      const formattedData = formatBillsForExport(filteredBills);
      exportToExcel(formattedData, 'bills', 'Bills');
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const handleWhatsAppShare = async (bill) => {
    try {
      // Fetch branch data to get review link
      let branch;
      if (branches.length > 0) {
        branch = branches.find(b => b.id === bill.branch_id);
      } else {
        // For managers/leads, fetch branch data
        const response = await api.get(`/branches`);
        branch = response.data.find(b => b.id === bill.branch_id);
      }
      
      if (!branch || !branch.google_review_link) {
        toast.error('Review link not configured for this branch. Please add it in Master Data.');
        return;
      }

      // Check if customer phone is available
      if (!bill.customer_phone) {
        toast.error('Customer phone number not available for this bill');
        return;
      }

      // Use template from settings or fallback to default
      const template = settings?.review_message_template || 
        "Thank you for visiting TheHappyScreens - {branch_name}! 🎉\n\nWe hope you enjoyed your experience at our {theater_name}.\n\nWe'd love to hear your feedback! Please share your experience:\n\n{review_link}\n\nThank you ! 😊";
      
      // Replace placeholders
      const message = template
        .replace(/{branch_name}/g, bill.branch_name)
        .replace(/{theater_name}/g, bill.theater_name)
        .replace(/{review_link}/g, branch.google_review_link);
      
      // Format phone number for WhatsApp (remove spaces, dashes, and add country code if needed)
      let phoneNumber = bill.customer_phone.replace(/\s+/g, '').replace(/-/g, '');
      
      // If number doesn't start with country code, add India code (91)
      if (!phoneNumber.startsWith('+') && phoneNumber.length === 10) {
        phoneNumber = '91' + phoneNumber;
      } else if (phoneNumber.startsWith('+')) {
        phoneNumber = phoneNumber.substring(1); // Remove + sign
      }
      
      // WhatsApp Click-to-Chat API with phone number and message
      // Works for both saved and unsaved contacts
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      
      // Open WhatsApp - will open app on mobile, web on desktop
      window.open(whatsappUrl, '_blank');
      
      toast.success(`Opening WhatsApp chat with ${bill.customer_phone}`);

      // Mark as sent
      await api.patch(`/bills/${bill.id}/review-status?action=sent`);
      fetchBills(); // Refresh to show updated status
    } catch (error) {
      console.error('WhatsApp share error:', error);
      toast.error('Failed to open WhatsApp');
    }
  };

  const handleMarkReviewed = async (billId) => {
    try {
      await api.patch(`/bills/${billId}/review-status?action=received`);
      toast.success('Marked as reviewed');
      fetchBills();
    } catch (error) {
      toast.error('Failed to update review status');
    }
  };

  const handleUnmarkReviewed = async (billId) => {
    try {
      await api.patch(`/bills/${billId}/review-status?action=unreceived`);
      toast.success('Review status cleared');
      fetchBills();
    } catch (error) {
      toast.error('Failed to update review status');
    }
  };

  const handleOpenAuditDialog = (bill) => {
    setSelectedBill(bill);
    setAuditNotes(bill.audit_notes || '');
    setAuditFlagged(bill.audit_flagged || false);
    setAuditDialogOpen(true);
  };

  const handleCloseAuditDialog = () => {
    setAuditDialogOpen(false);
    setSelectedBill(null);
    setAuditNotes('');
    setAuditFlagged(false);
  };

  const handleSubmitAuditNotes = async () => {
    if (!auditNotes.trim()) {
      toast.error('Please enter audit notes');
      return;
    }

    try {
      await api.patch(`/bills/${selectedBill.id}/audit-notes`, null, {
        params: {
          notes: auditNotes,
          flagged: auditFlagged
        }
      });
      toast.success('Audit notes saved successfully');
      handleCloseAuditDialog();
      fetchBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save audit notes');
    }
  };

  const toggleBillExpansion = (billId) => {
    setExpandedBills(prev => ({
      ...prev,
      [billId]: !prev[billId]
    }));
  };

  const toggleDetailsExpansion = (billId) => {
    setExpandedDetails(prev => ({
      ...prev,
      [billId]: !prev[billId]
    }));
  };

  const handleSelectBill = (billId) => {
    setSelectedBillIds(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBillIds.length === filteredBills.length) {
      setSelectedBillIds([]);
    } else {
      setSelectedBillIds(filteredBills.map(bill => bill.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      await api.post('/bills/bulk-delete', selectedBillIds);
      toast.success(`${selectedBillIds.length} bill(s) deleted successfully`);
      setSelectedBillIds([]);
      setBulkDeleteDialogOpen(false);
      fetchBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bills');
    }
  };

  const isAuditor = user?.role === 'auditor';
  const isAdmin = user?.role === 'admin';
  const canViewAuditNotes = isAuditor || isAdmin;

  const renderBillDetails = (bill) => {
    // Group items by type
    const cakes = bill.items.filter(item => item.item_type === 'cake');
    const decorations = bill.items.filter(item => item.item_type === 'decoration');
    const foods = bill.items.filter(item => item.item_type === 'food');
    const miscellaneous = bill.items.filter(item => item.item_type === 'miscellaneous');

    return (
      <div className="bg-white p-6 rounded-lg space-y-6">
        <h3 className="text-lg font-semibold text-slate-900 border-b pb-2">Complete Bill Breakdown</h3>
        
        {/* Customer Details */}
        {(bill.customer_name || bill.customer_phone) && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-3">Customer Details</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {bill.customer_name && (
                <div>
                  <p className="text-slate-600">Name:</p>
                  <p className="font-semibold text-slate-900">{bill.customer_name}</p>
                </div>
              )}
              {bill.customer_phone && (
                <div>
                  <p className="text-slate-600">Phone:</p>
                  <p className="font-semibold text-slate-900">{bill.customer_phone}</p>
                </div>
              )}
              {bill.customer_email && (
                <div>
                  <p className="text-slate-600">Email:</p>
                  <p className="font-semibold text-slate-900">{bill.customer_email}</p>
                </div>
              )}
              {bill.billed_by && (
                <div>
                  <p className="text-slate-600">Billed By:</p>
                  <p className="font-semibold text-slate-900">{bill.billed_by}</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Theater Details */}
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
            <span className="bg-indigo-600 text-white px-2 py-1 rounded text-xs">1</span>
            Theater Booking
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-600">Theater:</p>
              <p className="font-semibold text-slate-900">{bill.theater_name}</p>
            </div>
            <div>
              <p className="text-slate-600">Time Slot:</p>
              <p className="font-semibold text-slate-900">{bill.time_slot}</p>
            </div>
            <div>
              <p className="text-slate-600">Base Price (per person):</p>
              <p className="font-semibold text-slate-900">₹{bill.theater_price.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-600">Number of People:</p>
              <p className="font-semibold text-slate-900">{bill.num_people}</p>
            </div>
            <div className="col-span-2 border-t border-indigo-200 pt-2 mt-2">
              <p className="text-slate-600">Total Theater Charges:</p>
              <p className="font-bold text-lg text-indigo-900">₹{bill.total_theater_price.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Cakes */}
        {cakes.length > 0 && (
          <div className="bg-pink-50 p-4 rounded-lg">
            <h4 className="font-semibold text-pink-900 mb-3 flex items-center gap-2">
              <span className="bg-pink-600 text-white px-2 py-1 rounded text-xs">2</span>
              Cakes
            </h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-pink-200">
                  <th className="text-left py-2 text-slate-600">Item</th>
                  <th className="text-center py-2 text-slate-600">Qty</th>
                  <th className="text-right py-2 text-slate-600">Price</th>
                  <th className="text-right py-2 text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {cakes.map((item, idx) => (
                  <tr key={idx} className="border-b border-pink-100">
                    <td className="py-2 text-slate-900">{item.item_name}</td>
                    <td className="py-2 text-center text-slate-900">{item.quantity}</td>
                    <td className="py-2 text-right text-slate-900">₹{item.price.toFixed(2)}</td>
                    <td className="py-2 text-right font-semibold text-slate-900">₹{item.total.toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="pt-2 text-right font-semibold text-slate-700">Cakes Subtotal:</td>
                  <td className="pt-2 text-right font-bold text-pink-900">₹{cakes.reduce((sum, item) => sum + item.total, 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Decorations */}
        {decorations.length > 0 && (
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
              <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs">3</span>
              Decorations
            </h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-purple-200">
                  <th className="text-left py-2 text-slate-600">Item</th>
                  <th className="text-center py-2 text-slate-600">Qty</th>
                  <th className="text-right py-2 text-slate-600">Price</th>
                  <th className="text-right py-2 text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {decorations.map((item, idx) => (
                  <tr key={idx} className="border-b border-purple-100">
                    <td className="py-2 text-slate-900">{item.item_name}</td>
                    <td className="py-2 text-center text-slate-900">{item.quantity}</td>
                    <td className="py-2 text-right text-slate-900">₹{item.price.toFixed(2)}</td>
                    <td className="py-2 text-right font-semibold text-slate-900">₹{item.total.toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="pt-2 text-right font-semibold text-slate-700">Decorations Subtotal:</td>
                  <td className="pt-2 text-right font-bold text-purple-900">₹{decorations.reduce((sum, item) => sum + item.total, 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Food & Beverages */}
        {foods.length > 0 && (
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
              <span className="bg-green-600 text-white px-2 py-1 rounded text-xs">4</span>
              Food & Beverages
            </h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-green-200">
                  <th className="text-left py-2 text-slate-600">Item</th>
                  <th className="text-center py-2 text-slate-600">Qty</th>
                  <th className="text-right py-2 text-slate-600">Price</th>
                  <th className="text-right py-2 text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {foods.map((item, idx) => (
                  <tr key={idx} className="border-b border-green-100">
                    <td className="py-2 text-slate-900">{item.item_name}</td>
                    <td className="py-2 text-center text-slate-900">{item.quantity}</td>
                    <td className="py-2 text-right text-slate-900">₹{item.price.toFixed(2)}</td>
                    <td className="py-2 text-right font-semibold text-slate-900">₹{item.total.toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="pt-2 text-right font-semibold text-slate-700">Food & Beverages Subtotal:</td>
                  <td className="pt-2 text-right font-bold text-green-900">₹{foods.reduce((sum, item) => sum + item.total, 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Miscellaneous Items */}
        {miscellaneous.length > 0 && (
          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
              <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs">5</span>
              Miscellaneous Items
            </h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-orange-200">
                  <th className="text-left py-2 text-slate-600">Item</th>
                  <th className="text-center py-2 text-slate-600">Qty</th>
                  <th className="text-right py-2 text-slate-600">Price</th>
                  <th className="text-right py-2 text-slate-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {miscellaneous.map((item, idx) => (
                  <tr key={idx} className="border-b border-orange-100">
                    <td className="py-2 text-slate-900">{item.item_name}</td>
                    <td className="py-2 text-center text-slate-900">{item.quantity}</td>
                    <td className="py-2 text-right text-slate-900">₹{item.price.toFixed(2)}</td>
                    <td className="py-2 text-right font-semibold text-slate-900">₹{item.total.toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="pt-2 text-right font-semibold text-slate-700">Miscellaneous Subtotal:</td>
                  <td className="pt-2 text-right font-bold text-orange-900">₹{miscellaneous.reduce((sum, item) => sum + item.total, 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Final Summary */}
        <div className="bg-slate-100 p-4 rounded-lg border-2 border-slate-300">
          <h4 className="font-semibold text-slate-900 mb-3">Bill Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-700">Total Bill Amount:</span>
              <span className="font-semibold text-slate-900">₹{bill.total_bill_amount.toFixed(2)}</span>
            </div>
            {bill.discount_amount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Discount {bill.discount_reason ? `(${bill.discount_reason})` : ''}:</span>
                <span className="font-semibold">- ₹{bill.discount_amount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-300 pt-2">
              <span className="font-semibold text-slate-900">Final Amount:</span>
              <span className="font-bold text-lg text-slate-900">₹{bill.actual_amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-700">Advance Paid:</span>
              <span className="font-semibold text-green-700">₹{(bill.advance_paid || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-300 pt-2">
              <span className="font-semibold text-slate-900">Balance Due:</span>
              <span className="font-bold text-lg text-red-600">₹{(bill.balance_due || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Details */}
          <div className="mt-4 pt-4 border-t border-slate-300">
            <h5 className="font-semibold text-slate-900 mb-2">Payment Details</h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-700">Payment Mode:</span>
                <span className="font-semibold text-slate-900 capitalize">{bill.payment_mode}</span>
              </div>
              {bill.payment_mode === 'both' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-700">Cash Amount:</span>
                    <span className="font-semibold text-slate-900">₹{(bill.cash_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700">Online Amount:</span>
                    <span className="font-semibold text-slate-900">₹{(bill.online_amount || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
              {bill.scanner_name && (
                <div className="flex justify-between">
                  <span className="text-slate-700">Scanner Used:</span>
                  <span className="font-semibold text-slate-900">{bill.scanner_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Created By */}
          <div className="mt-4 pt-4 border-t border-slate-300 text-xs text-slate-500">
            <p>Created by: {bill.created_by_username} on {new Date(bill.created_at).toLocaleString()}</p>
          </div>
        </div>
      </div>
    );
  };

  const filteredBills = bills.filter(bill => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      bill.theater_name.toLowerCase().includes(search) ||
      bill.branch_name.toLowerCase().includes(search) ||
      bill.date.includes(search)
    );
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="view-bills-title">
          Bills
        </h1>
        <Button onClick={handleExport} variant="outline" className="gap-2" disabled={filteredBills.length === 0}>
          <Download size={16} />
          Export to Excel
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={20} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  data-testid="search-input"
                  placeholder="Search bills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Branch Filter (Admin, Auditor & Regional Manager) */}
            {(user?.role === 'admin' || isAuditor || isRegionalManager) && (
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select 
                  value={filters.branch_id} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, branch_id: value, theater_id: '' });
                    if (value && value !== 'all') {
                      fetchTheaters(value);
                    } else {
                      setTheaters([]);
                    }
                  }}
                >
                  <SelectTrigger data-testid="filter-branch-select">
                    <SelectValue placeholder="All branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Theater Filter */}
            {(canViewAuditNotes || isRegionalManager) && (
              <div className="space-y-2">
                <Label>Theater</Label>
                <Select 
                  value={filters.theater_id} 
                  onValueChange={(value) => setFilters({ ...filters, theater_id: value })}
                  disabled={!filters.branch_id || filters.branch_id === 'all'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All theaters" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All theaters</SelectItem>
                    {theaters.map(theater => (
                      <SelectItem key={theater.id} value={theater.id}>{theater.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date Preset */}
            {(isAuditor || isRegionalManager) && (
              <div className="space-y-2">
                <Label>Date Range</Label>
                <Select 
                  value={filters.date_preset || 'all'} 
                  onValueChange={handleDatePresetChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All dates</SelectItem>
                    <SelectItem value="last7days">Last 7 days</SelectItem>
                    <SelectItem value="thismonth">This month</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Start Date */}
            {(user?.role === 'admin' || ((isAuditor || isRegionalManager) && filters.date_preset === 'custom')) && (
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  data-testid="filter-start-date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value, date_preset: 'custom' })}
                />
              </div>
            )}

            {/* End Date */}
            {(user?.role === 'admin' || ((isAuditor || isRegionalManager) && filters.date_preset === 'custom')) && (
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  data-testid="filter-end-date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value, date_preset: 'custom' })}
                />
              </div>
            )}

            {/* Flagged Bills Filter (For Auditors & Admins) */}
            {canViewAuditNotes && (
              <div className="space-y-2">
                <Label>Flagged Bills</Label>
                <div className="flex items-center space-x-2 h-10">
                  <Checkbox
                    id="flagged-filter"
                    checked={filters.flagged_only}
                    onCheckedChange={(checked) => setFilters({ ...filters, flagged_only: checked })}
                  />
                  <Label htmlFor="flagged-filter" className="cursor-pointer">
                    Show only flagged bills
                  </Label>
                </div>
              </div>
            )}

            {/* Apply Filters Button */}
            <div className="flex items-end gap-2">
              <Button data-testid="apply-filters-btn" onClick={fetchBills} className="flex-1">
                Apply Filters
              </Button>
              <Button 
                variant="outline" 
                onClick={handleResetFilters}
                className="flex-1"
                title="Clear all filters"
              >
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bills Table */}
      <Card>
        <CardContent className="p-0">
          {/* Bulk Actions Bar (Admin Only) */}
          {isAdmin && filteredBills.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-slate-50 border-b">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedBillIds.length === filteredBills.length && filteredBills.length > 0}
                  onCheckedChange={handleSelectAll}
                  id="select-all"
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  {selectedBillIds.length > 0 
                    ? `${selectedBillIds.length} selected` 
                    : 'Select all'}
                </Label>
              </div>
              {selectedBillIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete Selected ({selectedBillIds.length})
                </Button>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Loading bills...</p>
              </div>
            </div>
          ) : filteredBills.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No bills found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    {isAdmin && <th className="px-4 py-3 w-12"></th>}
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Branch</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Theater</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Time Slot</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">People</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Amount</th>
                    {canViewAuditNotes && (
                      <>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Advance</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Balance</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Discount</th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Payment</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredBills.map((bill) => (
                    <React.Fragment key={bill.id}>
                      <tr data-testid={`bill-row-${bill.id}`} className={`hover:bg-slate-50 ${
                        bill.is_cancelled ? 'bg-red-50 opacity-75' : ''
                      }`}>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={selectedBillIds.includes(bill.id)}
                              onCheckedChange={() => handleSelectBill(bill.id)}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm text-slate-900">{bill.date}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{bill.branch_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{bill.theater_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{bill.time_slot}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{bill.num_people}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-right text-slate-900">₹{bill.actual_amount.toFixed(2)}</td>
                        {canViewAuditNotes && (
                          <>
                            <td className="px-4 py-3 text-sm text-right text-slate-900">₹{(bill.advance_paid || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-900">₹{(bill.balance_due || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-sm text-right text-slate-900">₹{(bill.discount_amount || 0).toFixed(2)}</td>
                          </>
                        )}
                        <td className="px-4 py-3 text-sm capitalize">
                          <div className="flex gap-2 flex-wrap">
                            {bill.is_cancelled ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                                ✕ Cancelled
                              </span>
                            ) : (
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                bill.payment_mode === 'cash' ? 'bg-green-100 text-green-700' :
                                bill.payment_mode === 'online' ? 'bg-blue-100 text-blue-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {bill.payment_mode}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            {/* View Details Button - For Auditors and Admins */}
                            {canViewAuditNotes && (
                              <Button
                                onClick={() => toggleDetailsExpansion(bill.id)}
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title={expandedDetails[bill.id] ? "Hide bill details" : "View complete bill details"}
                              >
                                <Eye size={16} />
                              </Button>
                            )}

                            {/* Audit Notes Button - Only for Auditors and Admins */}
                            {canViewAuditNotes && (
                              <Button
                                onClick={() => handleOpenAuditDialog(bill)}
                                size="sm"
                                variant="ghost"
                                className={`${bill.audit_flagged ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50'}`}
                                title={bill.audit_notes ? "View/Edit audit notes" : "Add audit notes"}
                              >
                                {bill.audit_flagged ? <AlertCircle size={16} /> : <FileText size={16} />}
                              </Button>
                            )}

                            {/* Expand/Collapse Audit Details - Only for Auditors and Admins */}
                            {canViewAuditNotes && bill.audit_notes && (
                              <Button
                                onClick={() => toggleBillExpansion(bill.id)}
                                size="sm"
                                variant="ghost"
                                className="text-slate-600 hover:text-slate-700"
                                title={expandedBills[bill.id] ? "Collapse audit details" : "Expand audit details"}
                              >
                                {expandedBills[bill.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </Button>
                            )}

                            {/* WhatsApp Review Button - Hidden for Auditors */}
                            {!isAuditor && (
                              <Button
                                onClick={() => handleWhatsAppShare(bill)}
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Send review link via WhatsApp"
                              >
                                {bill.review_link_sent ? <Send size={16} className="text-green-600" /> : <MessageCircle size={16} />}
                              </Button>
                            )}

                            {/* Mark as Reviewed Button - Hidden for Auditors */}
                            {!isAuditor && (
                              <Button
                                onClick={() => bill.review_received ? handleUnmarkReviewed(bill.id) : handleMarkReviewed(bill.id)}
                                size="sm"
                                variant="ghost"
                                className={bill.review_received ? "text-blue-600 hover:text-blue-700 hover:bg-blue-50" : "text-slate-400 hover:text-slate-600"}
                                title={bill.review_received ? "Review received - Click to unmark" : "Mark as reviewed"}
                              >
                                <CheckCircle size={16} fill={bill.review_received ? "currentColor" : "none"} />
                              </Button>
                            )}

                            {/* Edit Button - Hidden for Auditors and Cancelled Bills */}
                            {!isAuditor && !bill.is_cancelled && (
                              <Button
                                data-testid={`edit-bill-${bill.id}`}
                                onClick={() => navigate(`/edit-bill/${bill.id}`)}
                                size="sm"
                                variant="ghost"
                                className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                              >
                                <Edit size={16} />
                              </Button>
                            )}

                            {/* Cancelled Bill Info - Show instead of Edit */}
                            {!isAuditor && bill.is_cancelled && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 cursor-default"
                                disabled
                                title={`Cancelled: ${bill.cancellation_reason || 'No reason provided'}`}
                              >
                                <Edit size={16} className="opacity-50" />
                              </Button>
                            )}
                            
                            {/* Delete Button - Only for Admin */}
                            {isAdmin && (
                              <Button
                                data-testid={`delete-bill-${bill.id}`}
                                onClick={() => handleDelete(bill.id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Expanded Bill Details Row */}
                      {canViewAuditNotes && expandedDetails[bill.id] && (
                        <tr className="bg-slate-50">
                          <td colSpan={isAdmin ? (canViewAuditNotes ? 11 : 9) : (canViewAuditNotes ? 10 : 8)} className="px-4 py-4">
                            {renderBillDetails(bill)}
                          </td>
                        </tr>
                      )}
                      
                      {/* Expanded Audit Notes Row */}
                      {canViewAuditNotes && expandedBills[bill.id] && bill.audit_notes && (
                        <tr className="bg-slate-50">
                          <td colSpan={isAdmin ? (canViewAuditNotes ? 11 : 9) : (canViewAuditNotes ? 10 : 8)} className="px-4 py-4">
                            <div className="space-y-2">
                              <div className="flex items-start gap-2">
                                <FileText size={16} className="text-slate-600 mt-1" />
                                <div className="flex-1">
                                  <p className="text-sm font-semibold text-slate-900 mb-1">Audit Notes:</p>
                                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{bill.audit_notes}</p>
                                  {bill.audit_flagged && (
                                    <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                                      <AlertCircle size={12} />
                                      Flagged for Review
                                    </span>
                                  )}
                                  <div className="mt-2 text-xs text-slate-500">
                                    Audited by: {bill.audited_by || 'Unknown'} • {bill.audited_at ? new Date(bill.audited_at).toLocaleString() : 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-sm text-slate-600 text-center">
        Showing {filteredBills.length} bill{filteredBills.length !== 1 ? 's' : ''}
      </div>

      {/* Audit Notes Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Notes</DialogTitle>
            <DialogDescription>
              Add or edit audit notes for this bill. {selectedBill && `Bill Date: ${selectedBill.date} | Theater: ${selectedBill.theater_name}`}
            </DialogDescription>
          </DialogHeader>

          {selectedBill && (
            <div className="space-y-4">
              {/* Complete Bill Details */}
              <div className="border-2 border-slate-200 rounded-lg p-2">
                {renderBillDetails(selectedBill)}
              </div>

              {/* Previous Audit Info */}
              {selectedBill.audit_notes && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-slate-600 mb-1">Previous Audit:</p>
                  <p className="text-sm text-slate-700 mb-2">{selectedBill.audit_notes}</p>
                  <p className="text-xs text-slate-500">
                    By: {selectedBill.audited_by || 'Unknown'} • {selectedBill.audited_at ? new Date(selectedBill.audited_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              )}

              {/* Audit Notes Input */}
              <div className="space-y-2">
                <Label htmlFor="audit-notes">Audit Notes *</Label>
                <Textarea
                  id="audit-notes"
                  placeholder="Enter discrepancies or observations..."
                  value={auditNotes}
                  onChange={(e) => setAuditNotes(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
              </div>

              {/* Flag for Review Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="audit-flagged"
                  checked={auditFlagged}
                  onCheckedChange={setAuditFlagged}
                />
                <Label htmlFor="audit-flagged" className="flex items-center gap-2 cursor-pointer">
                  <AlertCircle size={16} className="text-red-600" />
                  Flag this bill for review (indicates critical discrepancy)
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAuditDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmitAuditNotes} disabled={!auditNotes.trim()}>
              Save Audit Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Selected Bills</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedBillIds.length} bill(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              Delete {selectedBillIds.length} Bill(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
